import { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/enquiry`, formData);
      toast.success('Thank you for your enquiry! We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', business_name: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <Toaster position="top-right" richColors />
      
      {/* Hero Banner */}
      <section className="bg-[#01195d] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Contact Us
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            Get in touch with our team. We're here to answer your questions and discuss how we can support your business.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0f172a] mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#01195d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#01195d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">Visit Us</h3>
                    <p className="text-slate-600">
                      Unit 1, 2 Costello Place<br />
                      Seven Hills NSW 2147<br />
                      Australia
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#01195d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#01195d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">Call Us</h3>
                    <a 
                      href="tel:1800934114" 
                      data-testid="contact-phone"
                      className="text-[#01195d] font-medium hover:underline"
                    >
                      1800 934 114
                    </a>
                    <p className="text-slate-500 text-sm mt-1">Toll-free within Australia</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#01195d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#01195d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">Email Us</h3>
                    <a 
                      href="mailto:info@gspacific.com.au" 
                      data-testid="contact-email"
                      className="text-[#01195d] font-medium hover:underline"
                    >
                      info@gspacific.com.au
                    </a>
                    <p className="text-slate-500 text-sm mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#01195d]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#01195d]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] mb-1">Business Hours</h3>
                    <div className="text-slate-600 space-y-1">
                      <p><span className="font-medium">Mon - Fri:</span> 8:00 AM - 4:00 PM</p>
                      <p className="text-slate-500 text-sm">Closed on Weekends & Public Holidays</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="mt-12">
                <h3 className="font-semibold text-[#0f172a] mb-4">Find Us</h3>
                <div className="rounded-xl overflow-hidden shadow-md" data-testid="google-map">
                  <iframe
                    title="GS Pacific Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.6883456!2d150.9369!3d-33.7729!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743d%3A0x6b0e29c9f8b2a0!2s2%20Costello%20Pl%2C%20Seven%20Hills%20NSW%202147%2C%20Australia!5e0!3m2!1sen!2s!4v1"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#f8fafc] p-8 md:p-10 rounded-2xl">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Send Us a Message</h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
                
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      data-testid="contact-form-name"
                      className="mt-1.5 h-12 border-slate-200 focus:ring-[#01195d] focus:border-[#01195d]"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="contact-form-email"
                      className="mt-1.5 h-12 border-slate-200 focus:ring-[#01195d] focus:border-[#01195d]"
                      placeholder="you@business.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      data-testid="contact-form-phone"
                      className="mt-1.5 h-12 border-slate-200 focus:ring-[#01195d] focus:border-[#01195d]"
                      placeholder="04XX XXX XXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="business_name" className="text-sm font-medium text-slate-700">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      type="text"
                      required
                      value={formData.business_name}
                      onChange={handleInputChange}
                      data-testid="contact-form-business"
                      className="mt-1.5 h-12 border-slate-200 focus:ring-[#01195d] focus:border-[#01195d]"
                      placeholder="Your business name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      data-testid="contact-form-message"
                      className="mt-1.5 border-slate-200 focus:ring-[#01195d] focus:border-[#01195d]"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-form-submit"
                    className="w-full bg-[#01195d] hover:bg-[#01195d]/90 text-white h-14 rounded-md font-semibold text-base"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
